import { formatSettingValue, languageName, optionLabelKey } from '@lib/customization/appSettings';
import { LocalizationManager, useLocalization } from '@lib/localization';
import {
  Box, ButtonBase, MenuItem, Select, Slider, Stack, Switch, ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import {
  colorSwatchSelectedStyles, colorSwatchStyles, segmentedStyles, selectStyles, valueChipStyles, widthOptionStyles,
} from '@pages/AppSettings.styles';
import { type AppSettingDefinition, type AppSettingValue } from '@store/api/appSettingsEndpoints';
import { useEffect, useState } from 'react';

interface Props {
  definition: AppSettingDefinition;
  value: AppSettingValue;
  disabled: boolean;
  onChange: (value: AppSettingValue) => void;
  /** The colour a line width is previewed in — its section's line colour, if it has one. */
  lineColor?: string;
}

/** `#rrggbbaa` is valid CSS, but a swatch drawn at the palette's alpha would read as a different colour. */
const opaque = (color: string) => color.slice(0, 7);

/**
 * A slider holds its own position while it is dragged and reports once it is let go, so a
 * drag is one request rather than one per step.
 */
const RangeControl: React.FC<Props & { definition: Extract<AppSettingDefinition, { type: 'range' }> }> = ({
  definition, value, disabled, onChange,
}) => {
  const { t, currentLanguage } = useLocalization();
  const [position, setPosition] = useState(Number(value));

  useEffect(() => setPosition(Number(value)), [value]);

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Slider
        size="small"
        value={position}
        min={definition.min}
        max={definition.max}
        step={definition.step}
        disabled={disabled}
        onChange={(_event, next) => { if (typeof next === 'number') setPosition(next); }}
        // MUI rounds to the step's precision already, so what arrives here is a clean 0.6.
        onChangeCommitted={(_event, next) => { if (typeof next === 'number' && next !== value) onChange(next); }}
        sx={{ width: 200 }}
      />
      <Box sx={valueChipStyles}>{formatSettingValue(definition, position, t, currentLanguage)}</Box>
    </Stack>
  );
};

const SettingControl: React.FC<Props> = (props) => {
  const { definition, value, disabled, onChange, lineColor } = props;
  const { t, currentLanguage } = useLocalization();

  switch (definition.type) {
    case 'boolean':
      return <Switch checked={Boolean(value)} disabled={disabled} onChange={(_event, checked) => onChange(checked)} />;

    case 'choice':
      return (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={value}
          disabled={disabled}
          onChange={(_event, next: string | null) => { if (next !== null) onChange(next); }}
          sx={segmentedStyles}
        >
          {definition.options.map((option) => (
            <ToggleButton key={option} value={option}>{t(optionLabelKey(option))}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      );

    case 'color':
      return (
        <Stack direction="row" spacing={1.25} role="radiogroup">
          {definition.options.map((option) => (
            <Tooltip key={option} title={opaque(option).toUpperCase()} arrow>
              <span>
                <ButtonBase
                  role="radio"
                  aria-checked={option === value}
                  aria-label={opaque(option)}
                  disabled={disabled}
                  onClick={() => { if (option !== value) onChange(option); }}
                  sx={[
                    colorSwatchStyles,
                    { backgroundColor: opaque(option) },
                    option === value && colorSwatchSelectedStyles,
                    disabled && { opacity: option === value ? 1 : 0.4 },
                  ]}
                />
              </span>
            </Tooltip>
          ))}
        </Stack>
      );

    case 'lineWidth': {
      const widest = Math.max(...definition.options);

      return (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={value}
          disabled={disabled}
          onChange={(_event, next: number | null) => { if (next !== null) onChange(next); }}
          sx={[segmentedStyles, widthOptionStyles]}
        >
          {definition.options.map((option) => (
            <ToggleButton key={option} value={option} aria-label={String(option)}>
              <Tooltip title={option} arrow>
                {/* Sizes as px strings: MUI reads a bare 1 as 100%, which drew the thinnest line tallest. */}
                <Box
                  sx={{
                    width: '20px',
                    flexShrink: 0,
                    height: `${Math.max(1, Math.round((option / widest) * 12))}px`,
                    borderRadius: '999px',
                    backgroundColor: lineColor ? opaque(lineColor) : 'currentColor',
                    opacity: option === value || !lineColor ? 1 : 0.55,
                  }}
                />
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      );
    }

    case 'range':
      return <RangeControl {...props} definition={definition} />;

    case 'language': {
      // The app's languages, not this interface's: a device can only be set to one the app is
      // translated into. A tag set elsewhere that the list no longer holds still shows as itself.
      const names = new Map(LocalizationManager.appLocales.map((locale) => [locale.code, locale.name]));
      // Named by the browser in the reader's language; the app's own name for the few it cannot.
      const nameIn = (code: string, displayLocale: string) => {
        const name = languageName(code, displayLocale);

        return name === code ? names.get(code) ?? code : name;
      };
      const codes = [...new Set([...names.keys(), ...(value ? [String(value)] : [])])]
        .sort((a, b) => nameIn(a, currentLanguage).localeCompare(nameIn(b, currentLanguage), currentLanguage));

      return (
        <Select
          size="small"
          value={String(value)}
          disabled={disabled}
          displayEmpty
          onChange={(event) => onChange(event.target.value)}
          sx={selectStyles}
        >
          <MenuItem value="">{t('AppSettings_DeviceLanguage')}</MenuItem>
          {codes.map((code) => (
            <MenuItem key={code} value={code}>
              {nameIn(code, code)}
              {code !== currentLanguage && (
                <Box component="span" sx={{ color: 'text.secondary', ml: 1, fontSize: 12.5 }}>
                  {nameIn(code, currentLanguage)}
                </Box>
              )}
            </MenuItem>
          ))}
        </Select>
      );
    }
  }
};

export default SettingControl;
